<template>
    <chart-template :title="$t('Production Rates')">
        <div class="row charts" :style="{ height: chartHeight }">
            <v-chart
                v-if="comparisonReport"
                class="col"
                :option="comparisonReportOptions"
                autoresize
            />
            <v-chart class="col" :option="reportOptions" autoresize />
        </div>
    </chart-template>
</template>

<script>
import { formatDate } from '@utils/dateRangeFormatter.js';
import { colors } from 'quasar';
import ChartTemplate from './ChartTemplate.vue';
const { getPaletteColor } = colors;
import { mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';

import { BarChart, ScatterChart } from 'echarts/charts';
import {
    LegendComponent,
    TitleComponent,
    TooltipComponent
} from 'echarts/components';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import VChart from 'vue-echarts';

use([
    CanvasRenderer,
    BarChart,
    TitleComponent,
    TooltipComponent,
    LegendComponent,
    ScatterChart
]);

export default {
    name: 'PerformanceRatesChart',
    components: { VChart, ChartTemplate },
    props: {
        report: {
            type: Object,
            required: true
        },
        comparisonReport: {
            type: Object,
            required: false
        }
    },
    computed: {
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        reportOptions() {
            const legends = this.getTitle(
                this.report,
                this.$t('Current Period')
            );
            return this.getChartOptions(this.report, legends);
        },
        comparisonReportOptions() {
            const legends = this.getTitle(
                this.comparisonReport,
                this.$t('Comparison Period')
            );
            return this.getChartOptions(this.comparisonReport, legends);
        },
        /**
         * Determine y-axis options based on the number of results to plot on the chart.
         * @return {Object}
         */
        yAxisOptions() {
            const names = this.report.rows.map(row => row.name);
            const yAxisOptions = {
                type: 'category',
                axisLabel: {
                    color: '#333',
                    fontSize: 13,
                    interval: 'auto'
                },
                data: names
            };
            return yAxisOptions;
        },
        chartGrid() {
            if (this.comparisonReport) {
                return {
                    top: 30,
                    bottom: 80
                };
            }
            return null;
        },
        /**
         * Computes the height of the chart based on the number of lines in the y-axis data.
         * @returns {string} The height of the chart in pixels.
         */
        chartHeight() {
            const totalLines = this.report.rows.length;
            const height = totalLines ? totalLines * 30 + 100 : 0;
            return height > 600 ? `${height}px` : '600px';
        }
    },
    methods: {
        /**
         * Get chart title if comparison report is available.
         * @param {Object} report
         * @param {String} chartTitle
         * @returns {Object} object containing chart title and subtitle
         */
        getTitle(report, chartTitle) {
            let title = null;
            let subtitle = null;
            if (this.comparisonReport) {
                title = chartTitle;
                subtitle = formatDate(
                    report.dateInterval.start,
                    report.dateInterval.end,
                    this.userLocale
                );
            }
            return { title: title, subtitle: subtitle };
        },
        /**
         * Get the chart definition for the echart component.
         * @param {Object} report object
         * @param {Object} legends object containing chart title and subtitle
         * @returns {Object} full chart options object
         */
        getChartOptions(report, legends) {
            return {
                title: {
                    text: legends.title,
                    textStyle: {
                        fontSize: 14
                    },
                    subtext: legends.subtitle,
                    subtextStyle: {
                        fontSize: 14
                    },
                    bottom: 0,
                    left: 'center'
                },
                grid: {
                    ...this.chartGrid,
                    outerBoundsMode: 'same',
                    outerBoundsContain: 'axisLabel'
                },
                legend: {
                    data: [this.$t('Actual rate'), this.$t('Max run rate')],
                    orient: 'horizontal',
                    top: 5,
                    left: 5
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow'
                    }
                },
                xAxis: {
                    show: this.$q.screen.gt.xs,
                    type: 'value',
                    boundaryGap: [0, 0.01],
                    name: this.$t('count/min'),
                    nameLocation: 'middle',
                    nameGap: 30,
                    axisLabel: {
                        fontSize: 13,
                        color: 'black'
                    },
                    nameTextStyle: {
                        fontSize: 13,
                        color: 'black'
                    }
                },
                yAxis: this.yAxisOptions,
                series: [
                    {
                        type: 'bar',
                        name: this.$t('Actual rate'),
                        data: report.rows.map(row =>
                            row.performance.actualRate.toFixed(2)
                        ),
                        color: getPaletteColor('light-green')
                    },
                    {
                        type: 'scatter',
                        name: this.$t('Max run rate'),
                        data: report.rows.map(row =>
                            row.performance.plannedRate.toFixed(2)
                        ),
                        symbol: 'diamond',
                        symbolSize: this.report.rows.length > 10 ? 12 : 20, // Size of the points
                        itemStyle: {
                            color: getPaletteColor('warning') // Color of the points
                        }
                    }
                ]
            };
        }
    }
};
</script>

<style lang="scss" scoped>
@import './dashboard.scss';
.charts {
    width: 100%;
    padding-bottom: 15px;
}
</style>
